/* eslint-disable react-hooks/exhaustive-deps */
import { tokens } from "../../../theme";
import { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  Divider,
  IconButton,
  MenuItem,
  Typography,
  useTheme,
} from "@mui/material";
import Select from "../../../components/SearchableSelect";
import { Close, InsertDriveFileOutlined } from "@mui/icons-material";
import { useReuploadDbSoftwareBuildScripts } from "../../../api/softwareImages";

// ── Design tokens ─────────────────────────────────────────────────────────────
const tone = (c) => ({
  primary: c.btn,
  primaryHover: c.btnHover,
  primaryTinted: c.actionTintBg,
  successFg: c.onDot,
  successBorder: c.isDark ? c.okFg : "#CCEED6",
  successBg: c.isDark ? c.okBg : "#FBFEFC",
  title: c.ctlInkHover,
  body: c.ctlInk,
  secondary: c.railMuted,
  helper: c.railHeading,
  separators: c.sepMuted,
  controlBorder: c.ctl,
  cardBorder: c.menuBorder,
  divider: c.railRule,
  bodySurface: c.drawerBody,
  disabledBg: c.disBg,
  disabledColor: c.disFg,
  disabledBorder: c.menuBorder,
  blocking: c.isDark ? c.warnText : "#9A6100",
  surface: c.surface,
  removeHoverBg: c.isDark ? c.errBg : "#EEF2F7",
  removeHoverInk: c.isDark ? c.errText : "#C0322B",
});

const fmtSize = (bytes) => {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

// ── File row ──────────────────────────────────────────────────────────────────
const FileRow = ({ label, file, onFileChange, onRemove, inputId }) => {
  const theme = useTheme();
  const T = tone(tokens(theme.paletteId));
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        padding: "12px 14px",
        border: file
          ? `1px solid ${T.successBorder}`
          : `1px dashed ${T.controlBorder}`,
        borderRadius: "8px",
        background: file ? T.successBg : T.surface,
      }}
    >
      <InsertDriveFileOutlined
        sx={{
          fontSize: 20,
          color: file ? T.successFg : T.helper,
          flexShrink: 0,
        }}
      />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: "13.5px",
            fontWeight: 600,
            color: T.body,
            lineHeight: 1.3,
          }}
        >
          {label}
        </Typography>
        {file ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              gap: "6px",
              mt: "2px",
            }}
          >
            <Typography
              sx={{
                fontSize: "12px",
                fontFamily: "monospace",
                color: T.body,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                minWidth: 0,
              }}
            >
              {file.name}
            </Typography>
            <Typography
              sx={{ fontSize: "12px", color: T.helper, flexShrink: 0 }}
            >
              {fmtSize(file.size)}
            </Typography>
          </Box>
        ) : (
          <Typography sx={{ fontSize: "12.5px", color: T.helper, mt: "2px" }}>
            No file chosen · keeps the existing script
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          flexShrink: 0,
        }}
      >
        <input
          accept=".sh"
          type="file"
          id={inputId}
          style={{ display: "none" }}
          onChange={(e) => {
            const f = e.target.files[0];
            if (f) onFileChange(f);
            e.target.value = "";
          }}
        />
        <Box
          component="label"
          htmlFor={inputId}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            height: "30px",
            padding: "0 12px",
            border: `1px solid ${T.controlBorder}`,
            borderRadius: "5px",
            background: T.surface,
            cursor: "pointer",
            fontSize: "11px",
            fontWeight: 600,
            fontFamily: "system-ui, sans-serif",
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: file ? T.body : T.primaryHover,
            userSelect: "none",
            "&:hover": { borderColor: T.primary, background: T.primaryTinted },
          }}
        >
          {file ? "Replace" : "Choose file"}
        </Box>
        {file && (
          <IconButton
            size="small"
            onClick={onRemove}
            sx={{
              width: 26,
              height: 26,
              color: T.helper,
              "&:hover": {
                background: T.removeHoverBg,
                color: T.removeHoverInk,
              },
            }}
          >
            <Close sx={{ fontSize: 14 }} />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

// ── Dialog ────────────────────────────────────────────────────────────────────
const ReuploadDbPatchScriptDialog = ({
  open,
  resetDialog,
  callback,
  buildId,
  steps,
  rowContext,
  displayNotification,
}) => {
  const theme = useTheme();
  const T = tone(tokens(theme.paletteId));

  const [installFile, setInstallFile] = useState(null);
  const [validFile, setValidFile] = useState(null);
  const [stepName, setStepName] = useState(steps?.[0]?.stepName || "init");

  const {
    postData: reuploadScripts,
    response: reuploadResp,
    responseStatus: reuploadStatus,
  } = useReuploadDbSoftwareBuildScripts(buildId);

  const hasFile = Boolean(installFile || validFile);
  const scriptCount = [installFile, validFile].filter(Boolean).length;

  const handleConfirm = async () => {
    const payload = { stepName };
    if (installFile) payload.installationScript = await installFile.text();
    if (validFile) payload.validationScript = await validFile.text();
    reuploadScripts(payload);
    resetDialog();
  };

  useEffect(() => {
    if (open) {
      setInstallFile(null);
      setValidFile(null);
      setStepName(steps?.[0]?.stepName || "init");
    }
  }, [open]);

  useEffect(() => {
    displayNotification(reuploadResp, reuploadStatus, callback);
  }, [reuploadResp, reuploadStatus]);

  const { jiraId, patchVersionName, releaseBranch } = rowContext || {};
  const footerText = !hasFile
    ? "Choose at least one script to continue"
    : `Replaces ${scriptCount} script(s) · retries from ${stepName}`;

  return (
    <Dialog
      open={open}
      onClose={resetDialog}
      PaperProps={{
        elevation: 0,
        sx: {
          width: 620,
          maxWidth: 620,
          borderRadius: "10px",
          border: `1px solid ${T.cardBorder}`,
          boxShadow: "0 12px 40px rgba(16,24,40,.16)",
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          padding: "20px 24px 16px",
          borderBottom: `1px solid ${T.divider}`,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: 17,
              fontWeight: 700,
              color: T.title,
              lineHeight: 1.3,
            }}
          >
            Reupload scripts
          </Typography>
          {(jiraId || patchVersionName || releaseBranch) && (
            <Typography
              sx={{ fontSize: "12.5px", color: T.separators, mt: "4px" }}
            >
              {jiraId && (
                <Box component="span" sx={{ fontFamily: "monospace" }}>
                  {jiraId}
                </Box>
              )}
              {patchVersionName && `${jiraId ? " · " : ""}${patchVersionName}`}
              {releaseBranch && ` · ${releaseBranch}`}
            </Typography>
          )}
        </Box>
        <IconButton
          size="small"
          onClick={resetDialog}
          sx={{
            width: 28,
            height: 28,
            border: `1px solid ${T.controlBorder}`,
            borderRadius: "5px",
            color: T.helper,
            flexShrink: 0,
            ml: 2,
          }}
        >
          <Close sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>

      {/* Body */}
      <Box
        sx={{
          padding: "18px 24px 20px",
          background: T.bodySurface,
          display: "flex",
          flexDirection: "column",
          gap: "14px",
        }}
      >
        <FileRow
          label="Installation script"
          file={installFile}
          onFileChange={setInstallFile}
          onRemove={() => setInstallFile(null)}
          inputId="reupload-installation-script"
        />
        <FileRow
          label="Validation script"
          file={validFile}
          onFileChange={setValidFile}
          onRemove={() => setValidFile(null)}
          inputId="reupload-validation-script"
        />
        <Divider sx={{ borderColor: T.divider }} />
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "14px" }}>
            <Typography
              sx={{
                width: 132,
                fontSize: "12.5px",
                fontWeight: 500,
                color: T.secondary,
                flexShrink: 0,
              }}
            >
              Step to retry from
            </Typography>
            <Select
              value={stepName}
              onChange={(e) => setStepName(e.target.value)}
              size="small"
              sx={{
                flex: 1,
                height: 34,
                fontSize: "13px",
                fontFamily: "monospace",
                borderRadius: "6px",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: T.controlBorder,
                  borderRadius: "6px",
                },
              }}
            >
              {(steps || []).map((step, i) => (
                <MenuItem
                  value={step?.stepName}
                  key={i}
                  sx={{ fontSize: "13px", fontFamily: "monospace" }}
                >
                  {step?.stepName}
                </MenuItem>
              ))}
            </Select>
          </Box>
          <Typography
            sx={{ fontSize: "12.5px", color: T.helper, mt: "6px", ml: "146px" }}
          >
            The patch build re-runs from this step onward.
          </Typography>
        </Box>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          padding: "14px 24px",
          borderTop: `1px solid ${T.cardBorder}`,
          background: T.surface,
          gap: "12px",
        }}
      >
        <Typography
          sx={{
            flex: 1,
            fontSize: "12.5px",
            color: hasFile ? T.secondary : T.blocking,
          }}
        >
          {footerText}
        </Typography>
        <Box
          component="button"
          onClick={resetDialog}
          sx={{
            height: 34,
            padding: "0 14px",
            border: `1px solid ${T.controlBorder}`,
            borderRadius: "6px",
            background: T.surface,
            cursor: "pointer",
            fontSize: "11.5px",
            fontWeight: 600,
            fontFamily: "system-ui, sans-serif",
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: T.body,
            "&:hover": { background: "#F2F4F7" },
          }}
        >
          Cancel
        </Box>
        <Box
          component="button"
          onClick={hasFile ? handleConfirm : undefined}
          sx={{
            height: 34,
            padding: "0 17px",
            border: `1px solid ${hasFile ? T.primary : T.disabledBorder}`,
            borderRadius: "6px",
            background: hasFile ? T.primary : T.disabledBg,
            cursor: hasFile ? "pointer" : "not-allowed",
            pointerEvents: "auto",
            fontSize: "11.5px",
            fontWeight: 600,
            fontFamily: "system-ui, sans-serif",
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: hasFile ? T.surface : T.disabledColor,
            "&:hover": hasFile
              ? { background: T.primaryHover, borderColor: T.primaryHover }
              : {},
          }}
        >
          Reupload
        </Box>
      </Box>
    </Dialog>
  );
};

export default ReuploadDbPatchScriptDialog;

export const useReuploadDbPatchScriptDialog = (
  callback = () => {},
  displayNotification = () => {}
) => {
  const [open, setOpen] = useState(false);
  const [buildId, setBuildId] = useState({});
  const [steps, setSteps] = useState([]);
  const [rowContext, setRowContext] = useState({});

  return {
    reuploadDbPatchScriptDialog: (
      <ReuploadDbPatchScriptDialog
        open={open}
        resetDialog={() => setOpen(false)}
        callback={callback}
        displayNotification={displayNotification}
        buildId={buildId}
        steps={steps}
        rowContext={rowContext}
      />
    ),
    setOpenReuploadDbPatchScriptDialog: (buildId, steps, rowContext) => {
      setOpen(true);
      setBuildId(buildId || "-");
      setSteps(steps || []);
      setRowContext(rowContext || {});
    },
  };
};
